import type { PrimaryPaletteColor } from "types/theme";

import { sharedNeutrals } from "./shared";

/**
 * User-selectable primary palettes.
 *
 * Each palette owns its own light and dark MUI `primary` palette.
 */
export const PRIMARY_PALETTE_KEYS = [
  "forest",
  "ocean",
  "desert",
  "luna",
  "night"
] as const;

export type PrimaryPaletteKey = (typeof PRIMARY_PALETTE_KEYS)[number];

export const DEFAULT_PRIMARY_PALETTE: PrimaryPaletteKey = "forest";

/**
 * Guards against a stale or manually edited persisted palette value.
 */
export const isPrimaryPaletteKey = (
  value: unknown
): value is PrimaryPaletteKey =>
  typeof value === "string" &&
  (PRIMARY_PALETTE_KEYS as readonly string[]).includes(value);

/**
 * Deliberately narrower than MUI's `PaletteColorOptions`, whose `light`, `dark`
 * and `contrastText` are optional: every palette below defines the full ramp
 * and all four semantic slots, and the narrow type is what lets the theme
 * picker read them off a definition as definite strings, without casting or
 * fallbacks. See `PrimaryPaletteColor` in `types/theme.d.ts`.
 */
export type PrimaryPaletteDefinition = {
  label: string;

  light: {
    primary: PrimaryPaletteColor;
  };

  dark: {
    primary: PrimaryPaletteColor;
  };
};

export const PRIMARY_PALETTES: Record<
  PrimaryPaletteKey,
  PrimaryPaletteDefinition
> = {
  desert: {
    dark: {
      primary: {
        50: "#321009",
        100: "#6E3325",
        200: "#642417",
        300: "#7C3728",
        400: "#96503F",
        500: "#B36B59",
        600: "#EBB8A6",
        700: "#D5AAA1",
        800: "#E4CBC6",
        contrastText: "#0A0D12",
        dark: "#4E170A",
        glow: "#FFB07A",
        light: "#96503F",
        main: "#321009"
      }
    },
    label: "Desert",
    light: {
      primary: {
        50: "#FBEFEA",
        100: "#E4CBC6",
        200: "#D5AAA1",
        300: "#C58B7D",
        400: "#B36B59",
        500: "#96503F",
        600: "#7C3728",
        700: "#642417",
        800: "#4E170A",
        contrastText: sharedNeutrals.white,
        dark: "#57281D",
        glow: "#FFB07A",
        light: "#642417",
        main: "#6E3325"
      }
    }
  },
  forest: {
    dark: {
      primary: {
        50: "#032A25",
        100: "#063C35",
        200: "#174D43",
        300: "#376957",
        400: "#5F8D73",
        500: "#87AF95",
        600: "#AFEFC7",
        700: "#C5DBC9",
        800: "#DDEAE2",
        contrastText: "#0A0D12",
        dark: "#063C35",
        glow: "#00FF67",
        light: "#174D43",
        main: "#032A25"
      }
    },
    label: "Forest",
    light: {
      primary: {
        50: "#EAFFF2",
        100: "#DDEAE2",
        200: "#C5DBC9",
        300: "#A9C6B0",
        400: "#87AF95",
        500: "#5F8D73",
        600: "#376957",
        700: "#174D43",
        800: "#063C35",
        contrastText: sharedNeutrals.white,
        dark: "#052E28",
        glow: "#00FF67",
        light: "#174D43",
        main: "#063C35"
      }
    }
  },
  luna: {
    dark: {
      primary: {
        50: "#2D0733",
        100: "#4C2563",
        200: "#5C1C65",
        300: "#73307E",
        400: "#8A4699",
        500: "#A159B3",
        600: "#D3ADE3",
        700: "#CBA0D2",
        800: "#DFC5E2",
        contrastText: "#0A0D12",
        dark: "#460A4E",
        glow: "#D68CFF",
        light: "#73307E",
        main: "#2D0733"
      }
    },
    label: "Luna",
    light: {
      primary: {
        50: "#F4ECF9",
        100: "#DFC5E2",
        200: "#CBA0D2",
        300: "#B77BC3",
        400: "#A159B3",
        500: "#8A4699",
        600: "#73307E",
        700: "#5C1C65",
        800: "#460A4E",
        contrastText: sharedNeutrals.white,
        dark: "#3B1C4E",
        glow: "#D68CFF",
        light: "#5C1C65",
        main: "#4C2563"
      }
    }
  },
  night: {
    dark: {
      primary: {
        50: "#1F1F1F",
        100: "#262626",
        200: "#454545",
        300: "#626262",
        400: "#838383",
        500: "#A8A8A8",
        600: "#CFCFCF",
        700: "#CECECE",
        800: "#E1E1E1",
        contrastText: "#0A0D12",
        dark: "#2F2F2F",
        glow: "#00FF67",
        light: "#A8A8A8",
        main: "#1F1F1F"
      }
    },
    label: "Night",
    light: {
      primary: {
        50: "#EFEFEF",
        100: "#E1E1E1",
        200: "#CECECE",
        300: "#BBBBBB",
        400: "#A8A8A8",
        500: "#838383",
        600: "#626262",
        700: "#454545",
        800: "#2F2F2F",
        contrastText: sharedNeutrals.white,
        dark: "#111111",
        glow: "#00FF67",
        light: "#838383",
        main: "#262626"
      }
    }
  },
  ocean: {
    dark: {
      primary: {
        50: "#00253F",
        100: "#143F60",
        200: "#174764",
        300: "#365F7A",
        400: "#567D95",
        500: "#7A9DB8",
        600: "#A9CDEB",
        700: "#B8CBD8",
        800: "#D5E0E7",

        contrastText: "#0A0D12",
        dark: "#003359",
        glow: "#4FC3FF",
        light: "#174764",
        main: "#00253F"
      }
    },
    label: "Ocean",
    light: {
      primary: {
        50: "#EAF3FA",
        100: "#D5E0E7",
        200: "#B8CBD8",
        300: "#9BB5C8",
        400: "#7A9DB8",
        500: "#567D95",
        600: "#365F7A",
        700: "#174764",
        800: "#003359",
        contrastText: sharedNeutrals.white,
        dark: "#0F3049",
        glow: "#4FC3FF",
        light: "#174764",
        main: "#143F60"
      }
    }
  }
};
